<template>
  <private-gym-page
    id="admin-booking-stages-page"
    name="admin-booking-stages"
    :navbar="false"
  >
    <!--
      The shared PageTopNavbar is deliberately switched off. It carries the
      panel toggle and the logo, which is right for a member's landing surface
      and wrong here: this is a pushed detail screen and the operator needs a
      way back to where they came from.

      No `:has_fab` either, unlike /admin/news: there is nothing to create. The
      three rows are seeded by D-41-01 and no row can be added or deleted.
    -->
    <f7-navbar title="Bevestigingsmomenten" back-link="Terug"></f7-navbar>

    <div class="booking-stages-column">
      <f7-block-header>
        Wanneer we leden vragen hun training te bevestigen, en wanneer een
        onbevestigde plek zou vrijkomen. Gerekend vanaf het begin van de training.
      </f7-block-header>

      <!--
        THREE MUTUALLY EXCLUSIVE STATES, driven by two flags and never by
        inferring state from an empty array.

        "we have not asked yet" and "we asked and the answer was broken" look
        identical in `draft`, and painting an editable form over numbers that
        were never loaded is the defect this v-if / v-else-if chain exists to
        prevent. `loading` and `error` are never both true.
      -->

      <!-- 1. Loading. -->
      <f7-block v-if="loading" class="text-align-center">
        <!--
          The INLINE preloader, not the app-wide overlay: the overlay covers the
          navbar and swallows the back gesture, so page chrome and title render
          immediately behind this block instead.
        -->
        <f7-preloader></f7-preloader>
      </f7-block>

      <!-- 2. Failure. -->
      <f7-block v-else-if="error" strong inset>
        <!--
          PERSISTENT, NOT A TOAST. A load failure leaves the operator with
          nothing else to act on, so the message must not disappear on a timer.

          The screen dispatches no notification of its own either: the store's
          fetch passes `suppress_error_toast`, so this block is the ONE message
          a load failure gets.

          The server's own error text is deliberately NOT rendered. It is
          English, unbounded, and names nothing the operator can act on; the
          authored Dutch copy below is the whole message.

          Retry runs through the same load method, which returns the screen to
          the loading block above rather than spinning inside this one. One
          loading representation per screen; a repeated failure updates this
          block rather than stacking a second.
        -->
        <p class="booking-stages-heading">We konden de bevestigingsmomenten niet laden.</p>
        <p>Controleer je internetverbinding en probeer het opnieuw.</p>
        <f7-button fill @click="fetchStages">Opnieuw proberen</f7-button>
      </f7-block>

      <!-- 3. The form. A response with fewer than three stages never reaches
           here: the store treats it as malformed and this chain shows the
           failure block instead (IC8). -->
      <template v-else>
        <template v-for="(row, index) in draft" :key="row.stage">
          <!--
            S6. THE STATE WORD GOES IN THE TITLE, and that is a measured choice
            rather than a layout preference. An operator must be able to tell
            which moments are live AT A GLANCE, and the summary line below is
            `f7-block-footer` at `rgba(0,0,0,0.45)` -- 3.36:1, the app's
            inherited secondary-text neutral. That is the wrong place for the
            single most consequential fact on the screen. `f7-block-title` is
            16px/600 at `#000` -- 21:1 -- and is already the largest, boldest
            text in each stage block.

            SENTENCE CASE, NEVER `LIVE` IN CAPS. All-caps is read
            letter-by-letter by some screen readers and is out of step with
            every other string in this app. The state word is FIRST in both the
            title suffix and the summary line, which is what makes it scannable.

            A SECOND COMPUTED ON THE SAME INPUTS (IC6), never a second
            derivation of them: `titles` and `summaries` both read
            `isLive(row)`, so the heading and the checkbox below it cannot
            disagree about the same stored value.
          -->
          <f7-block-title>{{ titles[index] }}</f7-block-title>

          <!--
            HOURS AND MINUTES, NOT ONE FIELD IN MINUTES (IC6). Stage 1 as a
            single `4320` is unreadable, and mistypable by a factor of ten with
            no visible difference on screen: `432` and `4320` look alike in a
            narrow input and mean three days apart.

            NO TWO-WAY BINDING, and that is a contract rather than a style
            preference. The page holds ONE draft; each input renders a value out
            of it and emits its change back through one handler. A second,
            drifting copy is what makes a comparison against the stored numbers
            lie -- NewsComposer.vue:3-11 says the same thing about the same
            hazard.

            NO ACCESSIBLE-NAME ATTRIBUTE IS ADDED ANYWHERE ON THIS SCREEN.
            Every label below is already a full noun phrase naming both the
            field and its unit; one that repeats the visible text is noise and
            one that differs from it is a defect.

            The attribute's own name is deliberately NOT spelled here. This
            repo has twice logged a grep matching the prose used to DENY a
            thing -- and it happened again on this very rule: the acceptance
            grep for it matched an earlier draft of this comment.
          -->
          <f7-list class="list-strong list-outline list-dividers">
            <f7-list-input
              label="Vraag — uren vóór de training"
              type="number"
              :value="row.fire_hours"
              @input="onFieldInput(index, 'fire_hours', $event.target.value)"
            ></f7-list-input>
            <f7-list-input
              label="Vraag — minuten vóór de training"
              type="number"
              :value="row.fire_minutes"
              @input="onFieldInput(index, 'fire_minutes', $event.target.value)"
            ></f7-list-input>
            <f7-list-input
              label="Vrijgeven — uren vóór de training"
              type="number"
              :value="row.release_hours"
              @input="onFieldInput(index, 'release_hours', $event.target.value)"
            ></f7-list-input>
            <f7-list-input
              label="Vrijgeven — minuten vóór de training"
              type="number"
              :value="row.release_minutes"
              @input="onFieldInput(index, 'release_minutes', $event.target.value)"
            ></f7-list-input>

            <!--
              S3 and S5 -- the two per-stage rollout controls (D-42-04).

              BELOW the four numbers, inside the SAME list, in the shape
              `UserId.vue:151-158` already ships inside an identical
              `list-strong list-outline list-dividers` list: the `:checked` +
              `@input` pair, `checkbox-icon="end"`, and a `name`. No `v-model`
              and no two-way binding -- the page holds ONE draft, rows render a
              value out of it and emit their change back through one handler.
              A second, drifting copy is what makes a comparison lie.

              CHECKBOXES, NOT TOGGLES, and the reason is the colour budget. An
              ON `<f7-toggle>` is a 52x32px black pill; six of them beside the
              save button below turns a configuration screen into a field of
              black. The 22px checkbox carries the same boolean at roughly a
              ninth of the filled area. A measured trade, not a preference.

              NO ACCESSIBLE-NAME ATTRIBUTE HERE EITHER. Both titles are full
              verb phrases stating what happens when the box is checked, so the
              visible label already IS the accessible name -- the real
              `f7-list-item` wraps its input in a `<label>`.

              NEITHER COLUMN NAME IS RENDERED. `name` carries the Dutch label
              slugged, the way the precedent's `studenten-korting` does: an
              operator reads a sentence about their gym, never a database
              column.
            -->
            <f7-list-item
              :value="row.releases_for_real"
              :checked="row.releases_for_real"
              @input="onFlagInput(index, 'releases_for_real', $event)"
              checkbox
              checkbox-icon="end"
              title="Geeft plekken echt vrij"
              name="geeft-plekken-echt-vrij"
            ></f7-list-item>
            <f7-list-item
              :value="row.refund_credits"
              :checked="row.refund_credits"
              @input="onFlagInput(index, 'refund_credits', $event)"
              checkbox
              checkbox-icon="end"
              title="Stort de credit terug"
              name="stort-de-credit-terug"
            ></f7-list-item>
          </f7-list>

          <!--
            ALWAYS RENDERED AND NEVER BLANK, one per stage, from ONE computed.

            The invalid variants are STATES, NOT ERRORS: same 14px block-footer
            styling as the valid one, no red, no icon, no extra class. An
            operator mid-edit is *doing something*, not *failing*. The server's
            validation is the authority -- this line is only what stops them
            reaching it -- and painting it red would spend the screen's one
            alarm colour on somebody halfway through typing a number.
          -->
          <f7-block-footer class="booking-stages-summary">
            {{ summaries[index] }}
          </f7-block-footer>
        </template>

        <f7-block>
          <!--
            ONE save for all three stages (IC7). The ordering rules BETWEEN
            stages are only checkable as a set, and three independent saves
            could leave the table in a combination no single save would have
            accepted. There is deliberately no per-stage save control to
            "obviously" add later (T-41-52).

            Framework7's own preloader/loading props carry the in-flight state,
            and `disabled` is what prevents the double submit -- never the
            app-wide overlay, which covers the navbar and its `Terug` link.

            Nothing on this page is sticky: the button scrolls with the form
            rather than covering the last input.
          -->
          <f7-button
            fill
            large
            :preloader="saving"
            :loading="saving"
            :disabled="saving"
            @click="save"
          >
            Bevestigingsmomenten opslaan
          </f7-button>
        </f7-block>
      </template>
    </div>
  </private-gym-page>
</template>

<script>
import { f7 } from "framework7-vue";

import PrivateGymPage from "../_layouts/PrivateGymPage.vue";

/**
 * ============================================================================
 *  A LARGER OFFSET IS EARLIER IN TIME
 * ============================================================================
 *
 * Every number on this screen is minutes BEFORE the training starts (D-41-04).
 * Stage 1's seeded 4320 is T−72h and stage 3's 30 is T−30min, so:
 *
 *   - "the release must be strictly AFTER the ask"      => release < fire
 *   - "this moment must come AFTER the previous moment" => fire < previous fire
 *
 * Both read backwards from the intuition, and they are the single most likely
 * thing to get wrong in this file. `UpdateBookingStagesRequest` on the server
 * carries the same sentence above the same two comparisons.
 */

/** The four editable fields of one stage, in the order they are rendered. */
const FIELDS = ["fire_hours", "fire_minutes", "release_hours", "release_minutes"];

/**
 * The five summary sentences, plus the one this screen shares with the server.
 *
 * Taken VERBATIM from 41-UI-SPEC's Copywriting Contract, which in turn matches
 * `UpdateBookingStagesRequest`'s own message constants, so the sentence an
 * operator reads while typing and the sentence the server answers with if they
 * submit anyway are the same sentence.
 */
const SUMMARY = Object.freeze({
  NOT_WHOLE: "Vul beide velden in met hele getallen.",
  ASK_AFTER_START:
    "Dit moment ligt op of ná het begin van de training en zou nooit verstuurd worden.",
  RELEASE_AT_START:
    "Een vrijgeefmoment op het begin van de training is onbereikbaar; kies minstens één minuut ervóór.",
  RELEASE_BEFORE_ASK:
    "Het vrijgeefmoment ligt vóór of op de vraag. Zet het vrijgeefmoment dichter bij de training.",
  NOT_BEFORE_PREVIOUS: "Dit moment ligt niet vóór het vorige moment.",
});

/**
 * Is this stage armed?
 *
 * ONE READ OF THE SERVER'S PER-STAGE VALUE, shared by the title, the summary
 * line and the checkbox, so the three cannot disagree. IC6 asks for a second
 * computed on the same inputs rather than a second derivation of them; this is
 * the input both computeds read.
 *
 * `=== true` rather than a truthiness test, deliberately. `BookingStage` casts
 * both columns, but this connection runs PDO with emulated prepares and an
 * UNCAST tinyint(1) arrives as the string `'0'` -- which is truthy in PHP and in
 * JavaScript alike. A stage that is rehearsing would then render as live, on the
 * one screen where that is the whole point. The cast is asserted server-side;
 * this is the client half of the same defence.
 *
 * @param {Object} row one draft row
 * @returns {boolean}
 */
function isLive(row) {
  return row.releases_for_real === true;
}

/**
 * Does this stage put the credit back?
 *
 * Same `=== true` reasoning as `isLive`. D-42-01: refund / refund / forfeit as
 * seeded, and a column the owner can flip on this very screen without a deploy —
 * which is exactly why no sentence below may state the outcome as a constant.
 *
 * @param {Object} row one draft row
 * @returns {boolean}
 */
function isRefunding(row) {
  return row.refund_credits === true;
}

/**
 * S4's body, authored and approved verbatim (42-UI-SPEC S4), 144 characters.
 *
 * It names the consequence -- a real deletion of a member's booking and a
 * message they receive -- and it names the SCOPE, `Dit geldt voor alle leden`,
 * because the operator is about to change what happens to every member of the
 * gym and not to a row they are looking at.
 */
const GO_LIVE_TEXT =
  "Vanaf de volgende ronde worden onbevestigde reserveringen op dit moment echt " +
  "verwijderd en krijgen leden een bericht. Dit geldt voor alle leden.";

/**
 * A field's value as a whole number of at least zero, or null.
 *
 * Returning null rather than NaN is what keeps `NaN uur` off the screen: every
 * caller checks for null first and falls back to the NOT_WHOLE sentence.
 *
 * An empty string is null on purpose. `Number("")` is 0, so a blank field would
 * otherwise read as a deliberate zero and the summary line would confidently
 * describe a configuration the operator has not finished typing.
 *
 * @param {*} value
 * @returns {?number}
 */
function wholeNumber(value) {
  if (value === null || typeof value === "undefined") {
    return null;
  }

  const text = String(value).trim();

  if (text === "" || !/^\d+$/.test(text)) {
    return null;
  }

  return Number(text);
}

/**
 * Minutes as the operator's own numbers read back in words.
 *
 * Never reached with a total below 1: the valid branch is the last of the six
 * and every zero total has already been named by one of the states above it.
 *
 * @param {number} total minutes before the training
 * @returns {string} e.g. "72 uur", "30 minuten", "4 uur en 30 minuten"
 */
function inWords(total) {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const minuteWord = minutes === 1 ? "minuut" : "minuten";

  if (hours > 0 && minutes > 0) {
    return hours + " uur en " + minutes + " " + minuteWord;
  }

  if (hours > 0) {
    return hours + " uur";
  }

  return minutes + " " + minuteWord;
}

/**
 * One stage's summary line: exactly one of NINE sentences, never empty.
 *
 * ---------------------------------------------------------------------------
 * NINE, NOT EIGHT, AND THE COUNT IS A RE-DERIVATION (42-UI-SPEC H1).
 * ---------------------------------------------------------------------------
 * 42-UI-SPEC S6 says "one computed, four valid-path branches plus phase 41's
 * four invalid ones -- eight in all". Phase 41 shipped FIVE invalid branches,
 * not four: D-42-06's `RELEASE_AT_START` was added to this chain late, after
 * the contract's count was written. The rule the contract states is unchanged
 * and is what matters -- ONE computed returning exactly one string, never a
 * `v-if` chain in the template. Only the arithmetic moved.
 *
 * ---------------------------------------------------------------------------
 * FOUR VALID VARIANTS, AND NO CREDIT SENTENCE IS EVER A CONSTANT (D-42-01).
 * ---------------------------------------------------------------------------
 * Live x refund, live x forfeit, rehearsing x refund, rehearsing x forfeit.
 * Every credit clause exists in exactly two forms selected at render time from
 * the SERVER's per-stage value; none is written once and left standing. Stage
 * 2's refund was the owner's own flip on 2026-08-30 and is a column they can
 * change on this screen without a deploy, so a hard-coded outcome would be
 * wrong the first time they use the control this file exists to give them.
 *
 * And a rehearsing stage never claims to free anything. `Geeft niets vrij,
 * schrijft alleen een logregel` is what phase 41's dry run actually does; a
 * line promising a release from a stage that only writes a ledger row is the
 * lie phase 41 spent a whole document forbidding.
 *
 * A chain of early returns in the shape of NewsComposer.vue's
 * `visibilitySummary`, and the ORDER MATTERS. It mirrors the server's: the base
 * rules (`min:1` on each offset) answer before the cross-field ones, because an
 * ordering question is not answerable over values that are not whole numbers,
 * and because a second complaint about a payload already rejected is noise. The
 * previous-moment rule comes last, matching `withValidator`'s own sequence.
 *
 * @param {Array<Object>} draft   the whole draft, because stage n's line
 *                                depends on stage n-1's ask
 * @param {number}        index   0-based position in the draft
 * @returns {string} never empty
 */
function summaryFor(draft, index) {
  const row = draft[index];
  const values = FIELDS.map((field) => wholeNumber(row[field]));

  if (values.some((value) => value === null)) {
    return SUMMARY.NOT_WHOLE;
  }

  const [fireHours, fireMinutes, releaseHours, releaseMinutes] = values;
  const fire = fireHours * 60 + fireMinutes;
  const release = releaseHours * 60 + releaseMinutes;

  // A LARGER OFFSET IS EARLIER IN TIME. -------------------------------------

  // Offset 0 is the training start itself, and the sweep selects `start > now`,
  // so such a stage could never be sent. Server: `fire_offset_minutes` min:1.
  if (fire < 1) {
    return SUMMARY.ASK_AFTER_START;
  }

  // D-42-06. A release offset of 0 is STRUCTURALLY UNREACHABLE -- the sweep
  // skips any stage whose release instant has not passed, and at offset 0 the
  // release instant and the training start are the same instant. Server:
  // `release_offset_minutes` min:1. Without this branch the line below would
  // read "geeft 0 minuten vóór de training vrij" -- a confident, valid-looking
  // sentence for a payload the server answers 422 to.
  if (release < 1) {
    return SUMMARY.RELEASE_AT_START;
  }

  // THE RELEASE MUST BE STRICTLY AFTER THE ASK. Later in time is a SMALLER
  // offset, so `release >= fire` is the failure: at equality the window is
  // empty, and above it the booking would be released before the member was
  // ever asked.
  if (release >= fire) {
    return SUMMARY.RELEASE_BEFORE_ASK;
  }

  // EACH MOMENT'S ASK AFTER THE PREVIOUS MOMENT'S. Ordered by stage number the
  // fire offsets must be STRICTLY DECREASING -- again because a larger offset is
  // earlier: moment 1 asks furthest out and therefore carries the largest
  // number. Two moments sharing an instant would ask twice in one sweep tick.
  if (index > 0) {
    const previous = FIELDS.slice(0, 2).map((field) => wholeNumber(draft[index - 1][field]));

    if (previous.every((value) => value !== null)) {
      const previousFire = previous[0] * 60 + previous[1];

      if (fire >= previousFire) {
        return SUMMARY.NOT_BEFORE_PREVIOUS;
      }
    }
  }

  // THE FOUR VALID VARIANTS. The state word comes FIRST in all four, which is
  // what makes the line scannable beside the title's own suffix.
  if (isLive(row)) {
    return (
      "Geeft plekken echt vrij. Vraagt " + inWords(fire) + " vóór de training, geeft " +
      inWords(release) + " vóór de training vrij. " +
      (isRefunding(row)
        ? "De credit gaat terug naar het lid."
        : "Het lid verliest de credit.")
    );
  }

  // The conditional mood throughout -- `zou ... vrijgeven`, `zou teruggaan` --
  // because a rehearsing stage frees nothing and moves no credit. The longest
  // of the four valid variants is this one at its refunding end: 156 characters,
  // which wraps inside `f7-block-footer` at 14px and has no line clamp.
  return (
    "Geeft niets vrij, schrijft alleen een logregel. Vraagt " + inWords(fire) +
    " vóór de training, zou " + inWords(release) + " vóór de training vrijgeven. " +
    (isRefunding(row)
      ? "De credit zou teruggaan naar het lid."
      : "Het lid zou de credit verliezen.")
  );
}

/**
 * One stage's heading: `Moment {n} — live` or `Moment {n} — proefdraai`.
 *
 * Reads `isLive(row)` -- the same function the summary line reads -- so the two
 * signals cannot drift apart. See the comment above the heading in the template
 * for why the word lives here rather than in the footer below it.
 *
 * @param {Object} row one draft row
 * @returns {string}
 */
function titleFor(row) {
  return "Moment " + row.stage + (isLive(row) ? " — live" : " — proefdraai");
}

/**
 * The server's three rows as an editable draft.
 *
 * Every value is flattened to a STRING, and a null to the empty string rather
 * than to `"null"`: binding a null straight into an input renders the literal
 * word for the operator to delete by hand (NewsComposerPage.vue's reasoning,
 * applied to numbers).
 *
 * @param {Array<Object>} stages
 * @returns {Array<Object>}
 */
function draftFromStages(stages) {
  return stages.map((row) => {
    const fire = Number.isFinite(row.fire_offset_minutes) ? row.fire_offset_minutes : null;
    const release = Number.isFinite(row.release_offset_minutes) ? row.release_offset_minutes : null;

    return {
      stage: row.stage,
      fire_hours: fire === null ? "" : String(Math.floor(fire / 60)),
      fire_minutes: fire === null ? "" : String(fire % 60),
      release_hours: release === null ? "" : String(Math.floor(release / 60)),
      release_minutes: release === null ? "" : String(release % 60),

      // The two flags stay REAL BOOLEANS rather than joining the strings above:
      // they are rendered by a checkbox, not typed into an input, and there is
      // no half-typed state for them to be in. `=== true` for the reason
      // `isLive` gives.
      releases_for_real: row.releases_for_real === true,
      refund_credits: row.refund_credits === true,
    };
  });
}

/**
 * The draft as the POST body's `stages` array.
 *
 * A field that is not a whole number becomes NaN rather than being silently
 * coerced to 0: the server's `integer` rule then rejects it by name, which is
 * the right outcome for an operator who pressed save with a half-typed field.
 * The summary line already told them; the server is still the authority.
 *
 * @param {Array<Object>} draft
 * @returns {Array<Object>}
 */
function stagesFromDraft(draft) {
  return draft.map((row) => ({
    stage: row.stage,
    fire_offset_minutes: Number(row.fire_hours) * 60 + Number(row.fire_minutes),
    release_offset_minutes: Number(row.release_hours) * 60 + Number(row.release_minutes),

    // BOTH FLAGS, ALWAYS. `UpdateBookingStagesRequest` marks them `required`
    // rather than `sometimes` -- IC7's one save covers all three stages, so a
    // three-key body is a 422 and not a partial update.
    releases_for_real: row.releases_for_real === true,
    refund_credits: row.refund_credits === true,
  }));
}

export default {
  components: { PrivateGymPage },

  props: {
    f7route: Object,
    f7router: Object,
  },

  data: function () {
    return {
      /**
       * Starts TRUE, deliberately.
       *
       * `mounted()` runs after the first render, so a false default would paint
       * the form -- or the failure block -- for one frame on every visit. IC8
       * names this explicitly, and it is asserted twice in the test file
       * because reading the flag after mount cannot tell the two apart.
       */
      loading: true,

      /**
       * Separate from `loading`, so a failed load is its own state rather than
       * an empty form. The two are never true together.
       */
      error: false,

      /**
       * THE ONE SOURCE OF TRUTH for what is on screen. The inputs render out of
       * it and emit back into it; nothing else holds a copy.
       */
      draft: [],
    };
  },

  computed: {
    /**
     * A save is in flight. Read from the store rather than mirrored locally, so
     * the button's disabled state cannot drift from the request it describes.
     */
    saving() {
      return this.$store.getters["booking_stages/saving"];
    },

    /**
     * The three summary lines, from ONE computed.
     *
     * One computed rather than three, and one `summaryFor` rather than a branch
     * per stage: six variants living in one chain is what makes "exactly one of
     * six, always" true by construction instead of by review.
     *
     * @returns {Array<string>} one non-empty sentence per stage
     */
    summaries() {
      return this.draft.map((row, index) => summaryFor(this.draft, index));
    },

    /**
     * The three headings, from a SECOND computed over the SAME inputs (IC6).
     *
     * Not a second derivation of them: `titleFor` calls `isLive`, which is the
     * one read of `releases_for_real` that `summaryFor` also makes. A screen
     * whose heading says `proefdraai` over a ticked box would be the exact lie
     * this phase exists to prevent, and one shared predicate is what makes that
     * impossible rather than merely unlikely.
     *
     * @returns {Array<string>} one heading per stage
     */
    titles() {
      return this.draft.map((row) => titleFor(row));
    },
  },

  mounted() {
    this.fetchStages();
  },

  methods: {
    /**
     * Loads the three stages.
     *
     * Retry runs through this same method, which is what returns the screen to
     * its preloader instead of spinning inside the failure block -- one loading
     * representation per screen, not two. A repeated failure updates the same
     * block rather than stacking a second one, because there is only ever one.
     *
     * `booking_stages/fetch_all` RESOLVES on failure by design, so there is no
     * `.catch` branch to read the failure out of: the store's own `load_failed`
     * flag is the surface, and a `.catch` here would be dead code that looked
     * load-bearing.
     */
    fetchStages() {
      this.loading = true;
      this.error = false;

      return this.$store
        .dispatch("booking_stages/fetch_all")
        .then(() => {
          this.error = this.$store.getters["booking_stages/load_failed"] === true;

          if (!this.error) {
            this.draft = draftFromStages(this.$store.getters["booking_stages/all"]);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * One field changed. The ONLY way anything reaches `draft`.
     *
     * @param {number} index 0-based stage position
     * @param {string} field one of FIELDS
     * @param {string} value the input's raw string, kept as typed
     */
    onFieldInput(index, field, value) {
      // A new row object rather than a mutation in place, so a watcher or a
      // future comparison sees a change it can detect.
      const next = { ...this.draft[index], [field]: value };

      this.draft = this.draft.map((row, position) => (position === index ? next : row));
    },

    /**
     * One flag was ticked or unticked. The ONLY way a flag reaches `draft`.
     *
     * @param {number} index 0-based stage position
     * @param {string} field `releases_for_real` or `refund_credits`
     * @param {Event}  event the native change event, from the row's `<input>`
     */
    onFlagInput(index, field, event) {
      const checked = event.target.checked === true;

      // ---------------------------------------------------------------------
      // ASYMMETRIC ON PURPOSE. DO NOT "TIDY" THIS INTO ONE PATH.
      // ---------------------------------------------------------------------
      // Turning a moment ON gets the confirmation. Turning it OFF is one tap
      // with no dialog at all -- D-42-04 says flipping back to dry run IS the
      // mitigation, and a mitigation you have to confirm is a mitigation you
      // reach slower. Confirm the direction that deletes members' bookings;
      // never obstruct the direction that stops it.
      //
      // `refund_credits` gets no dialog in either direction: it applies forward
      // only and destroys nothing that has already happened. A second
      // confirmation would train the operator to dismiss the one that matters.
      if (field === "releases_for_real" && checked) {
        // The draft stays FALSE here. It is set inside the dialog's confirm
        // handler, so a dismissed dialog is a refusal by default rather than by
        // remembering to undo something.
        this.confirmGoLive(index, event.target);

        return;
      }

      this.setFlag(index, field, checked);
    },

    /**
     * Asks before putting a moment live, in Dutch.
     *
     * `f7.dialog.create` with explicit labels, NEVER Framework7's one-line
     * confirm helper, which renders its untranslated OK/Cancel defaults
     * (framework7-bundle.js:14232-14233) because this app sets no dialog params
     * (app.vue:149-170). Every existing confirm in this Dutch app already ships
     * English buttons; this is the most consequential control in the phase and
     * does not get to be one of them. (42-UI-SPEC H6.)
     *
     * The destructive signal is the dialog and its verb, NOT a colour:
     * `.text-danger` is `red !important` = 4.00:1 on white, below AA for normal
     * text. `strong: true` gives `dialog-button-strong`, weight 500 at 21:1 --
     * the one label that must be read hardest must not be the hardest to read.
     *
     * NO `verticalButtons` and NO dialog-token override. Two horizontal buttons
     * of eight characters each fit the app-wide 270px width, and those tokens
     * govern every dialog already in this app -- impersonate, activate account,
     * open door, cancel booking. This confirmation must not look different from
     * the one a member sees when cancelling their own booking.
     *
     * FIRES AT TICK TIME, NOT AT SAVE TIME (IC3). The operator must be told what
     * checking the box means at the moment they check it, not twelve fields
     * later.
     *
     * @param {number}            index    0-based stage position
     * @param {HTMLInputElement}  inputEl  the row's own checkbox
     */
    confirmGoLive(index, inputEl) {
      const dialog = f7.dialog.create({
        title: "Moment " + this.draft[index].stage + " live zetten?",
        text: GO_LIVE_TEXT,
        closeByBackdropClick: true,
        buttons: [
          {
            text: "Annuleer",
            onClick: () => {
              this.$nextTick(() => {
                this.syncFlagElement(index, inputEl);
              });
            },
          },
          {
            text: "Zet live",
            strong: true,
            onClick: () => {
              // DEFERRED BY ONE TICK, deliberately. dialog-class.js:92-95 runs
              // this handler BEFORE it closes the dialog, so the state change
              // must not fly under a dialog that is still on screen.
              this.$nextTick(() => {
                this.setFlag(index, "releases_for_real", true);
              });
            },
          },
        ],
        on: {
          // A BACKDROP CLICK OR AN ESCAPE IS A REFUSAL TOO, and neither button's
          // handler runs on that path. Without this the operator would be left
          // looking at a ticked box over a moment that is still rehearsing --
          // the one disagreement between a control and the value behind it that
          // this whole screen exists to prevent. Idempotent with both buttons:
          // it reconciles the element to the draft, whatever the draft is by
          // then.
          closed: () => {
            this.syncFlagElement(index, inputEl);
          },
        },
      });

      dialog.open();
    },

    /**
     * Puts one checkbox element back in step with the draft.
     *
     * IMPERATIVE, AND IT HAS TO BE. The browser sets `.checked` itself as part
     * of the click, before any handler runs; Vue then skips patching a prop
     * whose vnode value did not change (`false` to `false`), so refusing the
     * tick in the draft alone leaves the drawn tick standing. This is the one
     * place on this screen that touches the DOM directly, and it only ever
     * copies the draft onto it -- never the other way round.
     *
     * @param {number}           index    0-based stage position
     * @param {?HTMLInputElement} inputEl
     */
    syncFlagElement(index, inputEl) {
      if (!inputEl || !this.draft[index]) {
        return;
      }

      inputEl.checked = this.draft[index].releases_for_real === true;
    },

    /**
     * Puts one flag into the draft.
     *
     * A new row object rather than a mutation in place, exactly as
     * `onFieldInput` does, so a watcher or a future comparison sees a change it
     * can detect.
     *
     * NOTHING IS SAVED HERE (IC3). The page holds ONE draft and phase 41's ONE
     * save writes all three stages; a per-control auto-save could store an
     * offset and a release flag that disagree, which is a combination no single
     * save would have accepted.
     *
     * @param {number}  index 0-based stage position
     * @param {string}  field `releases_for_real` or `refund_credits`
     * @param {boolean} value
     */
    setFlag(index, field, value) {
      const next = { ...this.draft[index], [field]: value };

      this.draft = this.draft.map((row, position) => (position === index ? next : row));
    },

    /**
     * Writes all three stages (IC7).
     *
     * ON SUCCESS the form is re-populated from the SERVER's response, not from
     * the draft: what the operator reads back is then what the next sweep will
     * actually use, which is what makes SC3 visible rather than merely
     * asserted. One success toast, `Bevestigingsmomenten opgeslagen` -- a
     * deliberate divergence from phase 39's no-toast-on-success rule, because
     * NOTHING VISIBLY MOVES here: the numbers after a save are the numbers that
     * were there before it, so without the toast a successful save is
     * indistinguishable from a no-op.
     *
     * ON FAILURE the operator's draft is KEPT. No reset, no re-fetch -- retyping
     * twelve numbers because the twelfth was wrong is the failure IC7 forbids --
     * and no message of this screen's own: the store does not suppress the
     * global handler, so `api/onError` has already shown the one message this
     * failure gets.
     */
    save() {
      return this.$store
        .dispatch("booking_stages/save", { stages: stagesFromDraft(this.draft) })
        .then(() => {
          this.draft = draftFromStages(this.$store.getters["booking_stages/all"]);

          this.$store.dispatch(
            "notifications/addSuccessNotification",
            // An OBJECT, never a bare string: notifications.js:47-51 assigns
            // cssClass/closeButtonColor/position onto whatever it is handed, and
            // a store module is an ES module -- strict mode -- where that throws
            // TypeError on a string primitive.
            { message: "Bevestigingsmomenten opgeslagen" },
          );
        })
        .catch(() => {
          // Deliberately empty. The draft stays exactly as it is and the global
          // handler owns the message; adding one here would make two about one
          // request. The catch exists so the rejection is handled rather than
          // logged as unhandled on top of the toast the operator already has.
        });
    },
  },
};
</script>

<style scoped>
/*
  Framework7 blocks and lists are full-bleed, and this app supports the
  desktop-web build as well as the phone. Twelve number inputs spanning 1,400px
  read as a spreadsheet rather than as a form.

  672px is the same rule /admin/news uses (`.admin-news-column`), taken from
  --f7-card-expandable-tablet-width (670px). Below 768px the page is full-bleed
  exactly as every other page in the app is.
*/
@media (min-width: 768px) {
  .booking-stages-column {
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
  }
}

/*
  The Heading role, taken entirely from Framework7's own tokens, matching
  `.admin-news-heading`. No numeric size is authored here or anywhere else on
  this page: a fifth size on one screen is how a design system stops being one.
*/
.booking-stages-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}
</style>
